import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { PaginationState } from "./usePagination";

export interface Bill {
  id: string;
  bill_number: string;
  bill_date: string;
  due_date: string | null;
  status: string;
  subtotal: number;
  retainage_amount: number;
  retainage_percent: number;
  total: number;
  description: string | null;
  created_at: string;
  paid_at: string | null;
  payment_reference: string | null;
  void_reason: string | null;
  project_id: string;
  vendor_id: string | null;
  contractor_id: string | null;
  work_order_id: string | null;
  projects?: { name: string; project_number: string | null } | null;
  vendors?: { name: string } | null;
}

export function useBills(pagination?: PaginationState) {
  return useQuery<Bill[]>({
    queryKey: ["bills", pagination?.page],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];

      if (pagination) {
        const { count } = await supabase
          .from("bills")
          .select("id", { count: "exact", head: true })
          .eq("company_id", companyId);
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("bills")
        .select("id, bill_number, bill_date, due_date, status, subtotal, retainage_amount, retainage_percent, total, description, created_at, paid_at, payment_reference, void_reason, project_id, vendor_id, contractor_id, work_order_id, projects(name, project_number), vendors(name)")
        .eq("company_id", companyId)
        .order("created_at", { ascending: false });

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Bill[];
    },
  });
}

export function useOpenBillsCount() {
  return useQuery<number>({
    queryKey: ["bills", "open_count"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return 0;

      const { count, error } = await supabase
        .from("bills")
        .select("id", { count: "exact", head: true })
        .eq("company_id", companyId)
        .in("status", ["draft", "pending", "approved"]);

      if (error) throw error;
      return count ?? 0;
    },
  });
}

// ── Bill Status Mutations ──────────────────────────────────────────────────

/** Log per-line entries to bill_payment_log (non-blocking) */
async function logBillLines(opts: {
  billId: string;
  action: string;
  userId?: string;
  paymentMethod?: string;
  paymentReference?: string;
  paymentDate?: string;
  amountPaid?: number;
}) {
  try {
    const { data: companyId } = await supabase.rpc("my_company_id");
    if (!companyId) return;

    const { data: lines } = await supabase
      .from("bill_lines")
      .select("id, description, line_total")
      .eq("bill_id", opts.billId);

    if (!lines || lines.length === 0) return;

    const { data: bill } = await supabase
      .from("bills")
      .select("total")
      .eq("id", opts.billId)
      .single();
    const billTotal = Number(bill?.total ?? 0);

    const rows = lines.map((line: any) => {
      const lineTotal = Number(line.line_total ?? 0);
      const proportion = billTotal > 0 ? lineTotal / billTotal : 0;
      const paymentAmount = opts.amountPaid != null ? Number((opts.amountPaid * proportion).toFixed(2)) : 0;

      return {
        company_id: companyId,
        bill_id: opts.billId,
        bill_line_id: line.id,
        action: opts.action,
        line_description: line.description ?? null,
        line_amount: lineTotal,
        payment_amount: paymentAmount,
        payment_method: opts.paymentMethod ?? null,
        payment_reference: opts.paymentReference ?? null,
        payment_date: opts.paymentDate ?? null,
        recorded_by: opts.userId ?? null,
      };
    });

    await supabase.from("bill_payment_log").insert(rows as any);
  } catch { /* non-blocking */ }
}

/** Approve a draft/pending bill */
export function useApproveBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (billId: string) => {
      const { error } = await supabase
        .from("bills")
        .update({ status: "approved", approved_at: new Date().toISOString() } as any)
        .eq("id", billId);
      if (error) throw error;
      const userId = (await supabase.auth.getUser()).data.user?.id;
      try {
        await supabase.from("ops_audit_log").insert({
          table_name: "bills", record_id: billId,
          action: "bill_approved", prev_status: "pending", new_status: "approved",
          changed_by: userId,
        } as any);
      } catch { /* non-blocking */ }

      // Per-line approval log
      await logBillLines({ billId, action: "approved", userId });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bills"] });
      qc.invalidateQueries({ queryKey: ["bill_payment_log"] });
      qc.invalidateQueries({ queryKey: ["project_expenses"] });
      qc.invalidateQueries({ queryKey: ["project_committed_cost"] });
    },
  });
}

/** Mark an approved bill as paid */
export function useMarkBillPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      billId, paymentReference, paidAt, paymentMethod, amountPaid,
    }: {
      billId: string;
      paymentReference?: string;
      paidAt?: string;
      paymentMethod?: string;
      amountPaid?: number;
    }) => {
      // Fetch bill total to determine paid vs partially_paid
      const { data: bill } = await supabase.from("bills").select("total, status").eq("id", billId).single();
      const billTotal = Number(bill?.total ?? 0);
      const paid = amountPaid ?? billTotal;
      const finalStatus = paid < billTotal ? "partially_paid" : "paid";

      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { error } = await supabase
        .from("bills")
        .update({
          status: finalStatus,
          paid_at: paidAt ?? new Date().toISOString(),
          paid_by: userId,
          payment_reference: paymentReference ?? null,
          payment_method: paymentMethod ?? null,
          amount_paid: paid,
        } as any)
        .eq("id", billId);
      if (error) throw error;
      try {
        await supabase.from("ops_audit_log").insert({
          table_name: "bills", record_id: billId,
          action: finalStatus === "paid" ? "bill_paid" : "bill_partially_paid",
          prev_status: bill?.status ?? "approved", new_status: finalStatus,
          changed_by: userId,
          metadata: { payment_reference: paymentReference ?? null, payment_method: paymentMethod ?? null, amount_paid: paid },
        } as any);
      } catch { /* non-blocking */ }

      // Per-line payment log
      await logBillLines({
        billId,
        action: finalStatus,
        userId,
        paymentMethod,
        paymentReference,
        paymentDate: paidAt ?? new Date().toISOString(),
        amountPaid: paid,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bills"] });
      qc.invalidateQueries({ queryKey: ["wo_bills"] });
      qc.invalidateQueries({ queryKey: ["bill_payment_log"] });
      qc.invalidateQueries({ queryKey: ["project_expenses"] });
      qc.invalidateQueries({ queryKey: ["project_committed_cost"] });
      qc.invalidateQueries({ queryKey: ["project_actual_cost"] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    },
  });
}

/** Void a bill with a required reason */
export function useVoidBill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ billId, reason }: { billId: string; reason: string }) => {
      const { data: bill } = await supabase.from("bills").select("status").eq("id", billId).single();
      const { data: updated, error } = await supabase
        .from("bills")
        .update({ status: "voided", void_reason: reason })
        .eq("id", billId)
        .select("id")
        .single();
      if (error) throw error;
      if (!updated) throw new Error("Permission denied — bill was not voided");
      const userId = (await supabase.auth.getUser()).data.user?.id;
      try {
        await supabase.from("ops_audit_log").insert({
          table_name: "bills", record_id: billId,
          action: "bill_voided", prev_status: bill?.status ?? null, new_status: "voided",
          changed_by: userId,
          metadata: { void_reason: reason },
        } as any);
      } catch { /* non-blocking */ }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bills"] });
      qc.invalidateQueries({ queryKey: ["project_expenses"] });
      qc.invalidateQueries({ queryKey: ["project_committed_cost"] });
      qc.invalidateQueries({ queryKey: ["project_actual_cost"] });
      qc.invalidateQueries({ queryKey: ["dashboard_metrics"] });
    },
  });
}